import Vue from 'vue'
import Vuex from 'vuex'
import configFile from '@/../app-config'
import createPersistedState from 'vuex-persistedstate'
import reflect from '../utils/reflect-promise'
import { AppHolder } from '@/utils/app-holder'

import auth from './modules/auth'
import user from './modules/user'
import accounts from './modules/accounts'
import bi from './modules/budget-insight'
import contracts from './modules/contracts'
import loans from './modules/loans'
import realEstates from './modules/real-estates'
import contractsCards from './modules/contracts-cards'
import series from './modules/series'
import funds from './modules/funds'
import banks from './modules/banks'
import investments from './modules/investments'
import error from './modules/error'
import toasts from './modules/toasts'
import notifications from './modules/notifications'
import advisor from './modules/advisor'
import customers from './modules/customers'
import interactions from './modules/interactions'
import theme from './modules/theme'
import window from './modules/window'
import missions from './modules/missions'
import club from './modules/club'
import ux from './modules/ux'

import {
  RESET_MODULE,
  START_APP_LOADING,
  STOP_APP_LOADING,
  CLOSE_WELCOME_CLUB
} from '@/store/mutation-types'

Vue.use(Vuex)

const store = new Vuex.Store({
  namespaced: true,

  state: {
    notProd: process.env.NODE_ENV !== 'production',
    brandname: configFile[process.env.APP_ID].params.appId,
    brandnameAdvisory: `${configFile[process.env.APP_ID].params.appId}-advisory`,
    patchnoteOpened: false,
    privateMode: false,
    privateModeAdvisory: false,
    cookieAccepted: false,
    openedIntros: {},
    appCfg: configFile[process.env.APP_ID].params,
    appLoading: false,
    welcomeClub: true,
    welcomeWesave: false,
    isSso: true,
    axeptio: {}
  },
  getters: {
    openedIntros ({ openedIntros }) {
      return openedIntros
    },
    isSso ({ isSso }) {
      return isSso
    },
    // Corresponds to an SSO connection and an account not created on the Wesave
    isEsrSsoRestriction(state) {
      return state.isSso && state.auth.userCreated
    }
  },
  actions: {
    startAppLoading ({ commit }) {
      commit(START_APP_LOADING)
    },

    stopAppLoading ({ commit }) {
      commit(STOP_APP_LOADING)
    },

    fetchAuthenticated ({ dispatch }, cached = true) {
      return dispatch('auth/fetchUser', cached)
        .then((user) => Promise.all([
          dispatch('accounts/fetch', { cached }),
          dispatch('realEstates/fetch', cached),
          dispatch('series/fetch', cached),
          dispatch('notifications/fetch', { cached }),
          dispatch('club/fetchClubSubscription')
        ]).then(() => user))
    },

    fetchAdvisory ({ dispatch }, cached = true) {
      return dispatch('advisor/fetchUser', cached)
        .then(() => Promise.all([
          reflect(dispatch('customers/fetchStats')),
          reflect(dispatch('notifications/fetch', { cached }))
        ]))
        .catch(() => {
          dispatch('auth/logOut')
        })
    },

    fetchCustomerView ({ dispatch }, id, cached = true) {
      return Promise.all([
        dispatch('customers/fetchUser', { id, cached })
          .then(() => dispatch('accounts/fetch', { id: id, cached: false })),
        dispatch('series/fetch', cached)
      ])
    },

    closeWelcomeClub ({ commit }) {
      commit(CLOSE_WELCOME_CLUB)
    },

    reset ({ commit }) {
      commit('auth/resetState')
      commit('user/resetState')
      commit(`accounts/${RESET_MODULE}`)
      commit('bi/resetState')
      commit(`contracts/${RESET_MODULE}`)
      commit(`loans/${RESET_MODULE}`)
      commit(`realEstates/${RESET_MODULE}`)
      commit('contractsCards/resetState')
      commit('series/resetState')
      commit('funds/resetState')
      commit(`banks/${RESET_MODULE}`)
      commit('investments/resetState')
      commit('toasts/resetState')
      commit('notifications/resetState')
      commit('advisor/resetState')
      commit(`customers/${RESET_MODULE}`)
      commit(`interactions/${RESET_MODULE}`)
      commit(`missions/${RESET_MODULE}`)
      commit(`club/${RESET_MODULE}`)
    },

    resetCustomer ({ commit }) {
      commit(`accounts/${RESET_MODULE}`)
      commit(`contracts/${RESET_MODULE}`)
      commit(`loans/${RESET_MODULE}`)
      commit(`realEstates/${RESET_MODULE}`)
      commit('user/resetState')
    }
  },

  modules: {
    auth: auth(),
    user: user(),
    accounts: accounts(),
    bi: bi(),
    contracts: contracts(),
    loans: loans(),
    realEstates: realEstates(),
    contractsCards: contractsCards(),
    series: series(),
    funds: funds(),
    banks: banks(),
    investments: investments(),
    error: error(),
    toasts: toasts(),
    notifications: notifications(),
    advisor: advisor(),
    customers: customers(),
    interactions: interactions(),
    theme: theme(),
    window: window(),
    missions: missions(),
    club: club(),
    ux: ux()
  },

  mutations: {
    [START_APP_LOADING] (state, data) {
      state.appLoading = true
    },

    [STOP_APP_LOADING] (state, data) {
      state.appLoading = false
    },

    [CLOSE_WELCOME_CLUB] (state) {
      state.welcomeClub = false
    },

    patchnoteOpened (state, data) {
      state.patchnoteOpened = data
    },
    switchPrivateMode (state) {
      state.privateMode = !state.privateMode
    },
    switchPrivateModeAdvisory (state) {
      state.privateModeAdvisory = !state.privateModeAdvisory
    },
    switchWelcomeWesave (state) {
      state.welcomeWesave = !state.welcomeWesave
    },
    acceptCookie (state) {
      state.cookieAccepted = true
    },
    setOpenedIntro (state, stepId) {
      state.openedIntros = {
        ...state.openedIntros,
        [stepId]: true
      }
    },
    setConnectionWithSSO (state, newValue) {
      state.isSso = newValue
    },
    setAxeptio (state, data) {
      state.axeptio = data
    }
  },

  plugins: [
    createPersistedState({
      paths: [
        'user.authenticated',
        'user.profile.onboardingOrigin',
        'auth.userCreated',
        'patchnoteOpened',
        'ux.sidebarMode',
        'ux.sidebarSection',
        'advisor.authenticated',
        'theme',
        'privateMode',
        'privateModeAdvisory',
        'cookieAccepted',
        'openedIntros',
        'welcomeClub',
        'isSso',
        'axeptio',
        'bi.BIToken',
        'bi.BIUser'
      ]
    })
  ]
})

AppHolder.store = store

export default store
